// useFilters.ts
import { useRoute, useRouter } from "vue-router";
import { onMounted } from "vue";
import { useSearchStore} from "~/store/search";

export function useFilters() {
    const route = useRoute();
    const router = useRouter();
    const searchStore = useSearchStore();

    let amenities = [];
    let categories = [];

    onMounted(() => {
        searchStore.setFiltersFromQuery(route.query);
    });

    function updateAmenitiesFilter(data: string[]) {
        amenities = data;
        // update router query
        router.push({
            query: {
                ...route.query,
                amenities: amenities.join(","),
            },
        });
    }

  function updateCategoriesFilter(data: string[]) {
    categories = data;
    // update router query
    router.push({
      query: {
        ...route.query,
        categories: categories.join(","),
      },
    });
  }

    function updatePriceFilter(data: [number, number]) {
        // update router query
        router.push({
            query: {
                ...route.query,
                price_gte: data[0],
                price_lte: data[1],
            },
        });
    }

    function updateRatingFilter(data: number) {
        // update router query
        router.push({
            query: {
                ...route.query,
                rating_gte: data,
            },
        });
    }

    // New filter update functions
    function updateBrandFilters(chains: string[], hotelName: string) {
        const query = { ...route.query } as Record<string, any>;
        
        if (chains.length > 0) {
            query.chains = chains.join(',');
        } else {
            delete query.chains;
        }
        
        if (hotelName.trim()) {
            query.hotel_name = hotelName.trim();
        } else {
            delete query.hotel_name;
        }
        
        router.push({ query });
    }

    function updateFreeCancellationFilter(enabled: boolean) {
        const query = { ...route.query } as Record<string, any>;
        
        if (enabled) {
            query.free_cancellation = 'true';
        } else {
            delete query.free_cancellation;
        }
        
        router.push({ query });
    }

    function updateBoardPlanFilter(boardPlans: string[]) {
        const query = { ...route.query } as Record<string, any>;
        
        if (boardPlans.length > 0) {
            query.board_plans = boardPlans.join(',');
        } else {
            delete query.board_plans;
        }
        
        router.push({ query });
    }

    function updatePaymentTimingFilter(options: { payAtPropertyOnly: boolean; payNowOnly: boolean }) {
        const query = { ...route.query } as Record<string, any>;
        
        if (options.payAtPropertyOnly) {
            query.pay_at_property_only = 'true';
        } else {
            delete query.pay_at_property_only;
        }
        
        if (options.payNowOnly) {
            query.pay_now_only = 'true';
        } else {
            delete query.pay_now_only;
        }
        
        router.push({ query });
    }

    function updateReviewQualityFilter(options: { minRating: number; minReviews: number }) {
        const query = { ...route.query } as Record<string, any>;
        
        if (options.minRating > 0) {
            query.min_rating = options.minRating.toString();
        } else {
            delete query.min_rating;
        }
        
        if (options.minReviews > 0) {
            query.min_reviews = options.minReviews.toString();
        } else {
            delete query.min_reviews;
        }
        
        router.push({ query });
    }

    function updateLocationFilters(options: { distanceBand: string; neighborhoods: string[]; customLocation?: any }) {
        const query = { ...route.query } as Record<string, any>;
        
        if (options.distanceBand) {
            query.distance_band = options.distanceBand;
        } else {
            delete query.distance_band;
        }
        
        if (options.neighborhoods.length > 0) {
            query.neighborhoods = options.neighborhoods.join(',');
        } else {
            delete query.neighborhoods;
        }

        if (options.customLocation) {
            query.custom_location = JSON.stringify(options.customLocation);
        } else {
            delete query.custom_location;
        }
        
        router.push({ query });
    }

    function updateAmenityComboFilters(options: { selectedCombos: string[]; requiredAmenityCodes: number[] }) {
        const query = { ...route.query } as Record<string, any>;
        
        if (options.selectedCombos.length > 0) {
            query.amenity_combos = options.selectedCombos.join(',');
        } else {
            delete query.amenity_combos;
        }
        
        router.push({ query });
    }

    function clearFilters() {
        const removableKeys = [
            'price_gte', 'price_lte', 'rating_gte', 'amenities', 'categories',
            'hotel_name', 'free_cancellation', 'board_plans',
            'pay_at_property_only', 'pay_now_only', 'min_rating', 'min_reviews',
            'distance_band', 'neighborhoods', 'amenity_combos', 'custom_location'
        ];
        const query = { ...route.query } as Record<string, any>;
        let hasChanges = false;

        removableKeys.forEach((key) => {
            if (key in query) {
                delete query[key];
                hasChanges = true;
            }
        });

        if (!hasChanges) {
            return;
        }

        amenities = [];
        categories = [];

        router.push({
            query,
        });
    }

    return {
        updateAmenitiesFilter,
        updatePriceFilter,
        updateRatingFilter,
        updateCategoriesFilter,
        updateBrandFilters,
        updateFreeCancellationFilter,
        updateBoardPlanFilter,
        updatePaymentTimingFilter,
        updateReviewQualityFilter,
        updateLocationFilters,
        updateAmenityComboFilters,
        clearFilters
    };
}
